import { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { OpenDrawerMenuButton } from "components/Carrier/components/OpenDrawerMenuButton";
import { DrawerMenu } from "components/DrawerMenu";
import { DashboardBackButton } from "components/Shipment/components/DashboardBackButton";
import { ClientAppEvent } from "constants/index";
import { useDisclosure } from "hooks";
import { useIsMobile } from "hooks/useIsMobile";
import { PageLayout } from "layouts";

import { ShipmentsFilter } from "../ShipmentsFilter";
import {
  COLLAPSED_RAIL_WIDTH_PX,
  CollapsedShipmentRail,
} from "./components/CollapsedShipmentRail";
import { ShipmentSideBar } from "./components/ShipmentSideBar";
import { getStoredSidebarCollapsed, setStoredSidebarCollapsed } from "./utils";

interface Props {
  shipmentOverview: React.ReactNode;
  shipments: CondensedShipment[];
  shipmentSelectionIsDisabled: boolean;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  page: number;
  pages: number;
  shipmentCount: number;
  activeFiltersCount: number;
}

export function Shipment(props: Props) {
  const {
    shipmentOverview,
    shipments,
    shipmentSelectionIsDisabled,
    handlePageSelected,
    page,
    pages,
    shipmentCount,
    activeFiltersCount,
  } = props;

  const showFullShipmentSideBar = !useIsMobile({ maxWidth: 1200 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    getStoredSidebarCollapsed,
  );
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const {
    isOpen: filtersOpen,
    onOpen: onFiltersOpen,
    onClose: onFiltersClose,
  } = useDisclosure(false);
  const {
    isOpen: filterToolsAreVisible,
    onOpen: onFilterToolsShow,
    onClose: onFilterToolsHide,
  } = useDisclosure(true);
  const posthog = usePostHog();

  const toggleSidebarCollapsed = (collapsed: boolean) => {
    posthog?.capture(ClientAppEvent.SHIPPER_SHIPMENT_SIDEBAR_TOGGLED, {
      collapsed,
    });
    onFiltersClose();
    setSidebarCollapsed(collapsed);
    setStoredSidebarCollapsed(collapsed);
  };

  const shipmentBrowser = (
    <ShipmentSideBar
      shipments={shipments as ShipperCondensedShipment[]}
      shipmentSelectionIsDisabled={shipmentSelectionIsDisabled}
      page={page}
      pages={pages}
      handlePageSelected={handlePageSelected}
      shipmentCount={shipmentCount}
      onFiltersClick={onFiltersOpen}
      activeFiltersCount={activeFiltersCount}
      onCollapse={
        showFullShipmentSideBar ? () => toggleSidebarCollapsed(true) : undefined
      }
    />
  );

  return (
    <PageLayout>
      {!showFullShipmentSideBar && (
        <Stack direction="column">
          <DashboardBackButton />
          <OpenDrawerMenuButton onOpen={onOpen} buttonLabel="MY SHIPMENTS" />
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={showFullShipmentSideBar ? 1 : undefined}
        sx={{ alignItems: "flex-start" }}
      >
        {showFullShipmentSideBar && (
          <Stack
            sx={{
              width: sidebarCollapsed ? COLLAPSED_RAIL_WIDTH_PX : "22%",
              minWidth: sidebarCollapsed ? COLLAPSED_RAIL_WIDTH_PX : 320,
              position: "sticky",
              top: 0,
              transition: "width 0.3s ease-in-out, min-width 0.3s ease-in-out",
            }}
          >
            {sidebarCollapsed ? (
              <CollapsedShipmentRail
                onClick={() => toggleSidebarCollapsed(false)}
                totalCount={shipmentCount}
                hasActiveFilter={activeFiltersCount > 0}
              />
            ) : (
              shipmentBrowser
            )}
          </Stack>
        )}
        <Stack
          spacing={showFullShipmentSideBar ? 1 : undefined}
          flex={1}
          sx={{
            overflow: "hidden",
            maxWidth: "100%",
            width: "100%",
          }}
        >
          {!showFullShipmentSideBar && (
            <DrawerMenu isOpen={isOpen} onClose={onClose}>
              {shipmentBrowser}
            </DrawerMenu>
          )}
          {shipmentOverview}
        </Stack>
      </Stack>
      <Dialog
        open={filtersOpen}
        onClose={onFiltersClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Filters</Typography>
              <Tooltip title="Close filters">
                <IconButton
                  onClick={onFiltersClose}
                  size="small"
                  aria-label="Close filters"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Divider />
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <ShipmentsFilter
            isOpen={filterToolsAreVisible}
            onOpen={onFilterToolsShow}
            onClose={onFilterToolsHide}
          />
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
